import React, { useState, useEffect } from 'react';
import { Mail, Eye, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Email course content for preview
const EMAIL_TEMPLATES = [
  {
    id: 'course_day_1',
    name: 'Day 1: Pre-Trip Inspection',
    category: 'Email Course',
    subject: 'Day 1: Pre-Trip Inspection Essentials',
    preview: 'The foundation of safe trucking starts before you turn the key'
  },
  {
    id: 'course_day_2',
    name: 'Day 2: Defensive Driving',
    category: 'Email Course',
    subject: 'Day 2: Defensive Driving Techniques',
    preview: 'Anticipate hazards before they become emergencies'
  },
  {
    id: 'course_day_3',
    name: 'Day 3: Fatigue Management',
    category: 'Email Course',
    subject: 'Day 3: Fatigue Management Strategies',
    preview: 'Tired driving is as dangerous as drunk driving'
  },
  {
    id: 'course_day_4',
    name: 'Day 4: Weather Driving',
    category: 'Email Course',
    subject: 'Day 4: Weather Driving Mastery',
    preview: 'Adapt your driving to conquer any conditions'
  },
  {
    id: 'course_day_5',
    name: 'Day 5: Load Securement',
    category: 'Email Course',
    subject: 'Day 5: Load Securement Best Practices',
    preview: 'A shifting load can turn your truck into a weapon'
  },
  {
    id: 'course_day_6',
    name: 'Day 6: Accident Response',
    category: 'Email Course',
    subject: 'Day 6: Accident Response Protocol',
    preview: 'Knowing what to do can protect you legally and physically'
  },
  {
    id: 'course_day_7',
    name: 'Day 7: Building Habits',
    category: 'Email Course',
    subject: 'Day 7: Building Lifelong Safety Habits',
    preview: 'Congratulations! Here\'s how to keep the momentum going'
  },
  {
    id: 'newsletter_welcome',
    name: 'Newsletter Welcome',
    category: 'Newsletter',
    subject: 'Welcome to TruckAccidentSupport Newsletter',
    preview: 'Thank you for subscribing to our safety updates'
  },
  {
    id: 'checklist_download',
    name: 'Checklist Download',
    category: 'Lead Magnet',
    subject: 'Your Accident Checklist is Ready',
    preview: 'Download your free accident response checklist'
  }
];

const EmailTemplatePreview = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('Email Course');
  const { getAuthHeaders } = useAuth();

  const categories = [...new Set(EMAIL_TEMPLATES.map(t => t.category))];

  const handlePreview = async (template) => {
    setSelectedTemplate(template);
    
    // Generate preview HTML based on template
    const html = generatePreviewHtml(template);
    setPreviewHtml(html);
  };

  const generatePreviewHtml = (template) => {
    const baseStyles = `
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
    `;

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${template.subject}</title>
      </head>
      <body style="${baseStyles}">
        <div style="background: linear-gradient(135deg, #ea580c 0%, #f97316 100%); padding: 20px; border-radius: 10px 10px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 24px;">🚛 TruckAccidentSupport</h1>
          <p style="color: rgba(255,255,255,0.9); margin: 5px 0 0 0;">${template.category}</p>
        </div>
        
        <div style="background: #f8f9fa; padding: 30px; border: 1px solid #e5e5e5; border-top: none;">
          <h2 style="color: #ea580c; margin-top: 0;">${template.subject}</h2>
          <p style="color: #666; font-style: italic;">${template.preview}</p>
          
          <div style="background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid #e5e5e5;">
            <p>Dear Driver,</p>
            <p>This is a preview of the <strong>${template.name}</strong> email template.</p>
            <p>The actual email will contain the full content with safety tips, actionable advice, and helpful resources.</p>
            <ul>
              <li>Professional formatting</li>
              <li>Mobile-responsive design</li>
              <li>Clear call-to-action buttons</li>
              <li>Unsubscribe link included</li>
            </ul>
            <p>Stay safe on the road!</p>
          </div>
          
          <a href="#" style="display: inline-block; background: #ea580c; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
            Learn More
          </a>
        </div>
        
        <div style="background: #1a1a1a; padding: 20px; border-radius: 0 0 10px 10px; text-align: center;">
          <p style="color: #999; font-size: 12px; margin: 0;">
            You're receiving this because you subscribed to TruckAccidentSupport.<br>
            <a href="#" style="color: #ea580c;">Unsubscribe</a> | 
            <a href="#" style="color: #ea580c;">Visit Website</a>
          </p>
          <p style="color: #666; font-size: 11px; margin-top: 10px;">
            © 2026 TruckAccidentSupport.com | Accident & Safety Resources
          </p>
        </div>
      </body>
      </html>
    `;
  };

  const handleSendTest = async () => {
    if (!testEmail || !selectedTemplate) {
      toast.error('Please enter an email address');
      return;
    }

    setSending(true);
    try {
      // In a real implementation, this would call an API to send the test email
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      toast.success(`Test email sent to ${testEmail}`);
      setTestEmail('');
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Email Templates</h2>
        <span className="text-sm text-gray-400">{EMAIL_TEMPLATES.length} templates available</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1 space-y-4">
          {categories.map(category => (
            <Card key={category} className="bg-gray-800 border-gray-700">
              <CardHeader 
                className="cursor-pointer py-3"
                onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-lg">{category}</CardTitle>
                  {expandedCategory === category ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </CardHeader>
              {expandedCategory === category && (
                <CardContent className="pt-0 space-y-2">
                  {EMAIL_TEMPLATES.filter(t => t.category === category).map(template => (
                    <div
                      key={template.id}
                      onClick={() => handlePreview(template)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedTemplate?.id === template.id
                          ? 'bg-orange-500/20 border border-orange-500'
                          : 'bg-gray-700/50 hover:bg-gray-700 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Mail className={`h-4 w-4 ${selectedTemplate?.id === template.id ? 'text-orange-500' : 'text-gray-400'}`} />
                        <span className="text-white text-sm font-medium">{template.name}</span>
                      </div>
                      <p className="text-gray-400 text-xs mt-1 truncate">{template.preview}</p>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Preview Panel */}
        <div className="lg:col-span-2">
          <Card className="bg-gray-800 border-gray-700 h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">
                  {selectedTemplate ? selectedTemplate.name : 'Select a template to preview'}
                </CardTitle>
                {selectedTemplate && (
                  <div className="flex items-center gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="border-gray-600 text-gray-300">
                          <Send className="h-4 w-4 mr-2" />
                          Send Test
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-gray-800 border-gray-700">
                        <DialogHeader>
                          <DialogTitle className="text-white">Send Test Email</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <p className="text-gray-400 text-sm">
                            Send a test version of "{selectedTemplate.name}" to verify formatting.
                          </p>
                          <Input
                            type="email"
                            placeholder="Enter email address"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            className="bg-gray-700 border-gray-600 text-white"
                          />
                          <Button
                            onClick={handleSendTest}
                            disabled={sending}
                            className="w-full bg-orange-500 hover:bg-orange-600"
                          >
                            {sending ? 'Sending...' : 'Send Test Email'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </div>
              {selectedTemplate && (
                <p className="text-gray-400 text-sm mt-1">
                  Subject: {selectedTemplate.subject}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {selectedTemplate ? (
                <div className="bg-white rounded-lg overflow-hidden" style={{ minHeight: '500px' }}>
                  <iframe
                    srcDoc={previewHtml}
                    title="Email Preview"
                    className="w-full h-[500px] border-0"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                  <Eye className="h-12 w-12 mb-4 opacity-50" />
                  <p>Select a template from the list to preview</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatePreview;
